import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Mail, Clock, Facebook, Twitter, Linkedin, ArrowRight, Send } from "lucide-react";
import { COMPANY, SERVICES, DISCLAIMER, saveNewsletter } from "../data/mock";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    saveNewsletter(email);
    setEmail("");
    toast.success("Subscribed!", { description: "You'll receive our latest insurance insights." });
  };

  return (
    <footer className="bg-[#073B67] text-[#C5DEEC]">
      <div className="mx-auto max-w-7xl px-6 pb-8 pt-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/10">
                <ShieldCheck className="h-6 w-6 text-[#B9DFF3]" />
              </span>
              <span className="leading-tight">
                <span className="block font-heading text-[19px] font-extrabold tracking-tight text-white">ABHI</span>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#91C4DE]">Insurance Agency</span>
              </span>
            </Link>
            <p className="mt-5 text-sm leading-relaxed">
              Since {COMPANY.since}, helping Canadians, immigrants and visitors with all their insurance needs. Your satisfaction is 100% guaranteed.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {[Facebook, Twitter, Linkedin].map((Icon, i) => (
                <a key={i} href="#!" aria-label="social" className="grid h-9 w-9 place-items-center rounded-full bg-white/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B9DFF3] hover:text-[#073B67]">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-base font-bold text-white">Our Services</h4>
            <ul className="mt-5 space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="group inline-flex items-center gap-1.5 transition-colors duration-200 hover:text-white">
                    <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                    <span className="transition-transform duration-300 group-hover:translate-x-1">{s.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-base font-bold text-white">Get in Touch</h4>
            <ul className="mt-5 space-y-3.5 text-sm">
              <li className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]" />{COMPANY.address}</li>
              <li><a href={COMPANY.phoneHref} className="flex items-center gap-2.5 transition-colors hover:text-white"><Phone className="h-4 w-4 shrink-0 text-[#B9DFF3]" />{COMPANY.phone}</a></li>
              <li><a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2.5 transition-colors hover:text-white"><Mail className="h-4 w-4 shrink-0 text-[#B9DFF3]" />{COMPANY.email}</a></li>
              <li className="flex items-start gap-2.5"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]" />{COMPANY.hours}</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-base font-bold text-white">Insurance Insights</h4>
            <p className="mt-5 text-sm leading-relaxed">Subscribe for practical guides on Super Visa, life and health coverage in Canada.</p>
            <form onSubmit={subscribe} className="mt-4 flex overflow-hidden rounded-full bg-white/10 p-1 ring-1 ring-white/15 focus-within:ring-[#B9DFF3]">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="newsletter-email"
                className="w-full bg-transparent px-4 text-sm text-white placeholder:text-[#72A9C8] focus:outline-none"
              />
              <button type="submit" data-testid="newsletter-submit" aria-label="Subscribe" className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#B9DFF3] text-[#073B67] transition-transform duration-300 hover:scale-105">
                <Send className="h-4 w-4" />
              </button>
            </form>
            <p className="mt-3 text-xs text-[#72A9C8]">By subscribing you agree to our data handling terms.</p>
          </div>
        </div>

        <div className="mt-14 rounded-2xl bg-white/[0.06] px-6 py-4">
          <p className="text-[11.5px] leading-relaxed text-[#91C4DE]">{DISCLAIMER}</p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-[#72A9C8] sm:flex-row">
          <p>© {new Date().getFullYear()} {COMPANY.legalName} All rights reserved.</p>
          <p>Owned & managed by {COMPANY.owner} · Licensed Insurance Advisor, BC</p>
        </div>
      </div>
    </footer>
  );
}
